// Single source of truth for fighter/weapon sprite paths, anchoring and the
// static draw used by BOTH the battle renderer and the buy-screen portrait, so
// a fighter looks identical in the lobby/buy preview and in the actual fight.

import type { Team, WeaponClass } from '@cs-shot/battle-sim';
import skinAnchorsRaw from './skinAnchors.json';
import skinAnchorsSplitRaw from './skinAnchorsSplit.json';
import weaponAnchorsRaw from './weaponAnchors.json';
import weaponSkinsRaw from './weaponSkins.json';
import type { RAnchors } from './render/spriteRender';
import { WEAPON_REL_W, AWP_REL_W, WEAPON_GRIP } from './render/spriteRender';
import { bustCache } from './render/battleAssetVersion';

export interface Sprite {
  /** the drawable source. Atlas sprites share ONE HTMLImageElement (the packed atlas) and
   *  carry a source rect (sx,sy,sw,sh) into it — drawn with 9-arg drawImage, never sliced into
   *  a canvas (iOS WebKit silently purges retained canvases → blank sprites). The per-file
   *  fallback loads standalone ImageBitmaps (GPU-resident, purge-safe) with no source rect. */
  canvas: ImageBitmap | HTMLCanvasElement | HTMLImageElement;
  /** source rect of the full sprite within `canvas` (atlas path). Absent → whole `canvas`. */
  sx?: number;
  sy?: number;
  sw?: number;
  sh?: number;
  /** content bounding box, relative to the sprite's own top-left (i.e. to sx,sy). */
  bx: number;
  by: number;
  bw: number;
  bh: number;
}

export type Stance = 'rifle' | 'awp';

// --- skins ---
// Each skin is just a file PREFIX. To ADD one: drop three sprites into
// /public/battle named `<prefix>_chibi.webp`, `<prefix>_awp.webp`,
// `<prefix>_dead.webp`, then add the prefix to its team here. Everything
// downstream (preloading, body lookup, the random per-fighter pick) is derived.
export const TEAM_SKINS: Record<Team, string[]> = {
  t: ['t1', 't2', 't3', 't4', 't5', 't6', 't7', 't8', 't9', 't10', 't11', 't12', 't13', 't14', 't15', 't16', 't17', 't18'],
  ct: ['ct1', 'ct2', 'ct3', 'ct4', 'ct5', 'ct6', 'ct7', 'ct8', 'ct9', 'ct10', 'ct11', 'ct12', 'ct13', 'ct14', 'ct15', 'ct16', 'ct17', 'ct18', 'ct19', 'ct20'],
};
const TEAMS: Team[] = ['t', 'ct'];

// The three sprites every skin provides, keyed by the body-lookup part name.
// `rifle`/`awp` are the live stances; `dead` is the corpse.
const SKIN_PARTS: { part: string; file: string }[] = [
  { part: 'rifle', file: 'chibi' },
  { part: 'awp', file: 'awp' },
  { part: 'dead', file: 'dead' },
];

const skinFile = (prefix: string, file: string) => `/battle/${prefix}_${file}.webp`;

/**
 * Every (key → src) the battle loader must fetch, across all skins/teams. Keys
 * are prefix-based — exactly what the renderer looks up: `${prefix}_${stance}`
 * (live) and `${prefix}_dead` (corpse).
 */
export function allBodySources(): { key: string; src: string }[] {
  const out: { key: string; src: string }[] = [];
  TEAMS.forEach((team) => {
    for (const prefix of TEAM_SKINS[team]) {
      for (const { part, file } of SKIN_PARTS) {
        out.push({ key: `${prefix}_${part}`, src: skinFile(prefix, file) });
      }
    }
  });
  return out;
}

// Stable per-fighter skin pick (returns a prefix) from its id — deterministic so
// the battle replay is identical on every client (cosmetic only, no sim effect).
/** A team's skins that are approved for the game (editor "Скин готов"); falls back
 *  to ALL of the team's skins if none are approved yet, so nothing renders blank.
 *  This is the ONLY set the lobby lets a player pick from and the random pick uses. */
export function readySkins(team: Team): string[] {
  const ready = TEAM_SKINS[team].filter((s) => readyFor(s));
  return ready.length ? ready : TEAM_SKINS[team];
}

export function skinForFighter(team: Team, id: string): string {
  const pool = readySkins(team);
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) | 0;
  return pool[Math.abs(h) % pool.length];
}

/** Resolve a fighter's skin to one that belongs to ITS team. A chosen skin is only used
 *  when it's a real prefix for that side (a `t*` skin has no CT sprite); anything else —
 *  null, or a stale wrong-side skin left over from a party side-switch — falls back to the
 *  deterministic same-side pick, so a T skin can never render on a CT fighter. */
export function resolveSkin(team: Team, id: string, skin?: string | null): string {
  // a chosen skin is honoured only if it's an APPROVED skin for this side; anything
  // else (not ready, null, or a stale wrong-side pick) → deterministic ready pick.
  return skin && TEAM_SKINS[team].includes(skin) && readyFor(skin) ? skin : skinForFighter(team, id);
}
export const WEAPON_SRC: Record<string, string> = {
  ak: bustCache('/battle/weapon_ak.webp'),
  m4: bustCache('/battle/weapon_m4.webp'),
  awp: bustCache('/battle/weapon_awp.webp'),
  lmg: bustCache('/battle/weapon_lmg.webp'),
};

// Weapon SKINS — variants of each weapon sprite. Authored in the skin editor (same
// `[{name,key}]` file it saves as weaponSkins.json). Index 0 is ALWAYS the stock weapon
// (`weapon_<key>.webp`); generated skins (`wpn_<name>.webp`) follow in list order. Selection
// isn't wired yet — the game always renders index 0 — but the plumbing is here so turning it
// on later is just passing a non-zero index.
const WEAPON_SKINS: { name: string; key: string }[] = weaponSkinsRaw;
/** Ordered sprite URLs for a weapon key: [stock, ...generated skins]. */
export function weaponSkinList(weaponKey: string): string[] {
  const base = WEAPON_SRC[weaponKey] ?? `/battle/weapon_${weaponKey}.webp`;
  const alts = WEAPON_SKINS.filter((s) => s.key === weaponKey).map((s) => `/battle/wpn_${s.name}.webp`);
  return [base, ...alts];
}
/** Sprite URL for a weapon key at a skin index (default 0 = stock). Falls back to stock. */
export function weaponSrcFor(weaponKey: string, skinIdx = 0): string {
  const list = weaponSkinList(weaponKey);
  return list[skinIdx] ?? list[0];
}
/** All authored weapon-skin variant sprites, keyed by skin name (for per-fighter rendering). */
export function allWeaponSkinSources(): { name: string; src: string }[] {
  return WEAPON_SKINS.map((s) => ({ name: s.name, src: `/battle/wpn_${s.name}.webp` }));
}
export const GRENADE_SRC = bustCache('/battle/weapon_grenade.webp');
export const SMOKE_NADE_SRC = bustCache('/battle/smoke_nade.webp');
export const FLASH_NADE_SRC = bustCache('/battle/flash_nade.webp');
export const KNIFE_SRC = bustCache('/battle/weapon_knife.webp');

// White silhouette icons (same shapes, RGB forced to white) for UI — buy screen,
// match log, etc. The battle sprites above stay colored (composited on fighters).
export const WEAPON_ICON_SRC: Record<string, string> = {
  ak: bustCache('/battle/icon_ak.webp'),
  m4: bustCache('/battle/icon_m4.webp'),
  awp: bustCache('/battle/icon_awp.webp'),
  lmg: bustCache('/battle/icon_lmg.webp'),
};
export const GRENADE_ICON_SRC = bustCache('/battle/icon_grenade.webp');
export const SMOKE_ICON_SRC = bustCache('/battle/icon_smoke.webp');
export const FLASH_ICON_SRC = bustCache('/battle/icon_flash.webp');
export const KNIFE_ICON_SRC = bustCache('/battle/icon_knife.webp');
export const MEDKIT_ICON_SRC = bustCache('/battle/icon_medkit.webp');
export const HEADSHOT_ICON_SRC = bustCache('/battle/icon_headshot.webp'); // CS killfeed headshot mark (deathnotice)
export const KNIFE_KILL_ICON_SRC = bustCache('/battle/icon_knife_kill.webp'); // CS killfeed knife glyph (feed only)
// knife held in hand during a lunge (in place of the gun): blade in the sprite
// points up-left, so KNIFE_ANGLE rotates it to face the target.
export const KNIFE_REL_W = 0.46; // knife width as fraction of body height
export const KNIFE_GRIP = { x: 0.62, y: 0.58 }; // grip point inside the knife content box
export const KNIFE_ANGLE = 2.1; // radians added to f.aim so the blade points at the target

// --- anchoring constants (shared with the battle renderer) ---
export const HAND_BY_STANCE: Record<Stance, { x: number; y: number }> = {
  rifle: { x: 0.66, y: 0.56 },
  awp: { x: 0.82, y: 0.46 },
};

// Per-skin overrides authored in the skin-editor tool, stored in skinAnchors.json:
// weapon-anchor per stance (fractions of the body content box) + a size `scale`
// multiplier (1 = default). Falls back to HAND_BY_STANCE / 1.
type AnchorOverride = { rifle?: { x: number; y: number }; awp?: { x: number; y: number }; scale?: number };
const SKIN_ANCHORS: Record<string, AnchorOverride> = skinAnchorsRaw;

/** Hand anchor for a given skin id (e.g. "t1", "ct5") and stance — per-skin
 * override if present, else the global default. `skin` may be undefined/''. */
export function handFor(skin: string | undefined, stance: Stance): { x: number; y: number } {
  const o = skin ? SKIN_ANCHORS[skin]?.[stance] : undefined;
  return o ?? HAND_BY_STANCE[stance];
}

/** Per-skin on-map size multiplier (1 = default). Lets each skin be dialled to a
 * consistent on-screen size despite differing pose/figure proportions. */
export function scaleFor(skin: string | undefined): number {
  // the split anchors (skin-editor source of truth) own the on-map size; fall back to
  // the legacy flat entry for any skin that only exists there.
  return (skin && (SKIN_SPLIT[skin]?.scale ?? SKIN_ANCHORS[skin]?.scale)) || 1;
}

// --- split-body anchors, authored in the standalone skin-editor and applied here
// as skinAnchorsSplit.json. Separate from the flat SKIN_ANCHORS above because the
// split `hand` is on the UPPER box — a different space that can't be merged.
type RunLike = { up?: { x?: number; y?: number; scale?: number }; scale?: number };
type SplitAnchor = {
  ready?: boolean;
  hand: RAnchors['hand'];
  up: RAnchors['up'];
  pivot: RAnchors['pivot'];
  scale?: number;
  deadScale?: number; // corpse (dead sprite) size multiplier
  body?: { rifle?: number; awp?: number };
  // may be per-stance {rifle,awp} OR the legacy single rig (migrated to both)
  run?: { rifle?: RunLike; awp?: RunLike } | RunLike;
};
const SKIN_SPLIT: Record<string, SplitAnchor> = skinAnchorsSplitRaw;
const RUN_ONE = { up: { x: 0, y: 0.86, scale: 1 }, scale: 1 };
const runOf = (r: RunLike | undefined) => ({
  up: { x: r?.up?.x ?? RUN_ONE.up.x, y: r?.up?.y ?? RUN_ONE.up.y, scale: r?.up?.scale ?? RUN_ONE.up.scale },
  scale: r?.scale ?? RUN_ONE.scale,
});
// accept per-stance run, or migrate a legacy single rig onto both stances
const isPerStanceRun = (r: SplitAnchor['run']): r is { rifle?: RunLike; awp?: RunLike } =>
  !!r && ('rifle' in r || 'awp' in r);
const runRigOf = (run: SplitAnchor['run']): RAnchors['run'] => {
  if (isPerStanceRun(run)) {
    return { rifle: runOf(run.rifle), awp: runOf(run.awp) };
  }
  return { rifle: runOf(run), awp: runOf(run) };
};

const bothStances = (o: { rifle?: unknown; awp?: unknown } | undefined) => !!(o && o.rifle && o.awp);

/** Is this skin finished & approved for the game (editor "Скин готов" checkbox)? */
export function readyFor(skin: string | undefined): boolean {
  return !!(skin && SKIN_SPLIT[skin]?.ready);
}

/** Per-skin corpse size multiplier (1 = default), authored in the skin-editor. */
export function deadScaleFor(skin: string | undefined): number {
  return (skin && SKIN_SPLIT[skin]?.deadScale) || 1;
}

/** Full split-body anchor set for the shared renderer, or null if the skin has no
 * COMPLETE authored anchors (needs hand/up/pivot for both stances) — an incomplete
 * skin is skipped rather than rendered broken. */
export function splitAnchorsFor(skin: string | undefined): RAnchors | null {
  const s = skin ? SKIN_SPLIT[skin] : undefined;
  if (!s || !bothStances(s.hand) || !bothStances(s.up) || !bothStances(s.pivot)) return null;
  return {
    hand: s.hand, up: s.up, pivot: s.pivot, scale: s.scale ?? 1,
    body: { rifle: s.body?.rifle ?? 1, awp: s.body?.awp ?? 1 },
    run: runRigOf(s.run),
  };
}

// the split parts a skin provides (in addition to the flat SKIN_PARTS).
// keys mirror what the renderer looks up: `${prefix}_${part}`.
const SPLIT_PARTS = ['legs', 'upper_rifle', 'upper_awp', 'legs_run'] as const;
export function splitBodySources(): { key: string; src: string }[] {
  const out: { key: string; src: string }[] = [];
  TEAMS.forEach((team) => {
    for (const prefix of TEAM_SKINS[team]) {
      for (const part of SPLIT_PARTS) out.push({ key: `${prefix}_${part}`, src: skinFile(prefix, part) });
    }
  });
  return out;
}
// weapon sizing/grip live in the framework-free shared renderer (the single source of truth,
// copied verbatim into the skin-editor); re-exported here so app callers keep one import path.
export { WEAPON_REL_W, AWP_REL_W, WEAPON_GRIP };

// Per-weapon MUZZLE point (bullet exit) as a fraction of the weapon content box, authored
// in the skin editor → weaponAnchors.json. Default {x:1, y:GRIP.y} = the far tip on the grip
// line, which reproduces the old formula-derived muzzle exactly.
const WEAPON_ANCHORS: Record<string, { x: number; y: number }> = weaponAnchorsRaw;
export function weaponMuzzleFor(weaponKey: string): { x: number; y: number } {
  return WEAPON_ANCHORS[weaponKey] ?? { x: 1, y: WEAPON_GRIP.y };
}

// --- battle-renderer tunables (animation timing/scale; no asset/anchor data) ---
export const TOKEN_H = 244; // on-screen body height in px (uniform — no depth shrink)
export const CORPSE_REL = 1.15; // corpse long-axis length as fraction of TOKEN_H
export const EXPLOSION_FRAMES = 6; // fallback frame count for the explosion strip

export const stanceOf = (w: WeaponClass): Stance => (w === 'awp' ? 'awp' : 'rifle');
// team T holds an AK, team CT an M4; AWP/LMG are shared
export const weaponKeyOf = (team: Team, w: WeaponClass) =>
  w === 'awp' ? 'awp' : w === 'lmg' ? 'lmg' : team === 't' ? 'ak' : 'm4';

/** Upgrade a drawable to a GPU-resident ImageBitmap; keep the original where the API is
 *  missing or the conversion fails (drawImage accepts either — only the speed differs). */
export function toBitmap<T extends HTMLImageElement | HTMLCanvasElement>(src: T): Promise<ImageBitmap | T> {
  if (typeof createImageBitmap !== 'function') return Promise.resolve(src);
  return createImageBitmap(src).catch(() => src);
}

/** Load an image as-is (assets already have transparent backgrounds) and return
 * a GPU ImageBitmap of it plus the content bounding box (for feet/hand anchoring).
 * A canvas is used only transiently for the alpha scan — retaining ~300 full-size
 * sprite canvases blew past the iOS WebView canvas-memory budget, and WebKit purges
 * canvas buffers silently (they draw as nothing: bodies vanished, guns floated). */
export function loadKeyedSprite(src: string): Promise<Sprite> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      // getContext / getImageData can return null or THROW on a memory-starved Android
      // WebView. Reject (→ surfaces the loading error screen) instead of letting the throw
      // escape this browser callback uncaught, which — with no error boundary — black-screens.
      try {
        const c = document.createElement('canvas');
        c.width = img.width;
        c.height = img.height;
        const cx = c.getContext('2d', { willReadFrequently: true });
        if (!cx) { reject(new Error(`no 2d context for ${src}`)); return; }
        cx.drawImage(img, 0, 0);
        // measure the opaque content box from the image's existing alpha — no recolouring
        const px = cx.getImageData(0, 0, c.width, c.height).data;
        let minX = c.width, minY = c.height, maxX = 0, maxY = 0;
        for (let i = 0; i < px.length; i += 4) {
          if (px[i + 3] > 24) {
            const p = i / 4;
            const x = p % c.width;
            const y = (p / c.width) | 0;
            if (x < minX) minX = x;
            if (x > maxX) maxX = x;
            if (y < minY) minY = y;
            if (y > maxY) maxY = y;
          }
        }
        // free the scan canvas eagerly — 0×0 releases its backing store now, not at GC
        c.width = 0;
        c.height = 0;
        void toBitmap(img).then((bmp) => resolve({ canvas: bmp, bx: minX, by: minY, bw: maxX - minX, bh: maxY - minY }));
      } catch (err) {
        reject(err instanceof Error ? err : new Error(`sprite decode failed: ${src}`));
      }
    };
    img.onerror = () => reject(new Error(`failed to load ${src}`));
    img.src = bustCache(src);
  });
}

